/**
 * Moves EasySell COD ("Comandă acum") buttons out of product-card image
 * galleries (where they land next to Quick Add) into a slot under product info.
 */
(() => {
  const GALLERY_BUTTON_SELECTOR = [
    '.card-gallery .es-popup-button-product',
    '.card-gallery [class*="es-popup-button"]:not(.es-popup-button-overwrite)',
    '.card-gallery [id*="es-popup-button"]',
    '.card-gallery [class*="easysell"]',
  ].join(', ');

  /**
   * Prefer moving a dedicated EasySell wrapper when the button sits alone in one.
   * @param {Element} button
   * @param {Element} gallery
   * @returns {Element}
   */
  function nodeToMove(button, gallery) {
    let node = button;
    let current = button.parentElement;

    while (current && current !== gallery && gallery.contains(current)) {
      const className = typeof current.className === 'string' ? current.className : '';
      const looksLikeEasySell =
        className.includes('es-') ||
        className.toLowerCase().includes('easysell') ||
        (typeof current.id === 'string' && current.id.includes('es-'));

      if (looksLikeEasySell && current.children.length === 1) {
        node = current;
      }

      // Stop climbing past interactive theme chrome (quick-add, badges).
      if (
        current.matches?.('quick-add-component, .quick-add, .product-badges, product-form-component, form')
      ) {
        break;
      }

      current = current.parentElement;
    }

    return node;
  }

  /**
   * @param {Element} el
   */
  function relocate(el) {
    if (!(el instanceof Element)) return;
    if (el.closest('[data-easysell-slot]')) return;
    if (el.getAttribute('data-easysell-moved') === 'true') return;

    const gallery = el.closest('.card-gallery');
    if (!gallery) return;

    const card = el.closest('product-card');
    if (!card) return;

    const slot = card.querySelector('[data-easysell-slot]');
    if (!slot) return;

    const moveTarget = nodeToMove(el, gallery);
    if (!gallery.contains(moveTarget)) return;

    slot.appendChild(moveTarget);
    moveTarget.setAttribute('data-easysell-moved', 'true');
    if (el !== moveTarget) el.setAttribute('data-easysell-moved', 'true');
  }

  /**
   * @param {ParentNode} [root]
   */
  function scan(root = document) {
    if (!root || !('querySelectorAll' in root)) return;
    root.querySelectorAll(GALLERY_BUTTON_SELECTOR).forEach(relocate);

    // Catch late-injected nodes that match only by being siblings of quick-add.
    root.querySelectorAll('product-card .card-gallery').forEach((gallery) => {
      gallery.querySelectorAll('button, a, div, span').forEach((candidate) => {
        const className = typeof candidate.className === 'string' ? candidate.className : '';
        const id = typeof candidate.id === 'string' ? candidate.id : '';
        if (
          className.includes('es-popup') ||
          className.toLowerCase().includes('easysell') ||
          id.includes('es-popup')
        ) {
          relocate(candidate);
        }
      });
    });
  }

  function init() {
    scan();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (
            node.matches?.(GALLERY_BUTTON_SELECTOR) ||
            (typeof node.className === 'string' &&
              (node.className.includes('es-popup') || node.className.toLowerCase().includes('easysell')))
          ) {
            relocate(node);
          }
          scan(node);
        }
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load', () => scan());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
